import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  Chip,
  Divider,
  useTheme,
  alpha,
} from '@mui/material'
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  RadioButtonChecked,
  RadioButtonUnchecked,
  CheckBox as CheckBoxIcon,
  CheckBoxOutlineBlank as CheckBoxOutlineBlankIcon,
} from '@mui/icons-material'
import { assessmentService } from '../api/assessments'

interface ShowResultsDialogProps {
  open: boolean
  onClose: () => void
  assessmentId: number
  assessmentTitle: string
}

interface QuestionResult {
  question_id: number
  question_text: string
  question_type: string
  points: number
  points_earned: number
  is_correct: boolean | null
  user_answer: string | null
  choices: {
    id: number
    text: string
    was_selected: boolean
  }[]
}

interface AttemptResults {
  submission_id: number
  attempt_number: number
  score: number
  points_earned: number
  total_points: number
  is_passed: boolean
  submitted_at: string
  questions: QuestionResult[]
}

const ShowResultsDialog: React.FC<ShowResultsDialogProps> = ({
  open,
  onClose,
  assessmentId,
  assessmentTitle,
}) => {
  const theme = useTheme()
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [results, setResults] = useState<AttemptResults | null>(null)

  useEffect(() => {
    if (open && assessmentId) {
      fetchResults()
    }
  }, [open, assessmentId])

  const fetchResults = async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await assessmentService.getLastAttemptResults(assessmentId)
      setResults(data)
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to load results')
    } finally {
      setLoading(false)
    }
  }

  const renderQuestionResult = (question: QuestionResult, index: number) => {
    const isCorrect = question.is_correct === true
    const isWrong = question.is_correct === false
    const isPending = question.is_correct === null

    return (
      <Card
        key={question.question_id}
        sx={{
          mb: 2,
          border: `2px solid ${
            isCorrect
              ? theme.palette.success.main
              : isWrong
              ? theme.palette.error.main
              : theme.palette.grey[300]
          }`,
          backgroundColor: alpha(
            isCorrect
              ? theme.palette.success.main
              : isWrong
              ? theme.palette.error.main
              : theme.palette.grey[500],
            0.05
          ),
        }}
      >
        <CardContent>
          {/* Question Header */}
          <Box display="flex" alignItems="flex-start" justifyContent="space-between" mb={2}>
            <Box flex={1}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Question {index + 1}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {question.question_text}
              </Typography>
            </Box>
            <Box ml={2}>
              {isCorrect && (
                <Chip
                  icon={<CheckCircleIcon />}
                  label="Correct"
                  color="success"
                  size="small"
                />
              )}
              {isWrong && (
                <Chip
                  icon={<CancelIcon />}
                  label="Wrong"
                  color="error"
                  size="small"
                />
              )}
              {isPending && (
                <Chip
                  label="Pending Review"
                  color="warning"
                  size="small"
                />
              )}
            </Box>
          </Box>

          <Divider sx={{ mb: 2 }} />

          {/* Points Info */}
          <Box display="flex" alignItems="center" gap={2} mb={2}>
            <Typography variant="body2" color="text.secondary">
              Points: <strong>{question.points_earned}</strong> / {question.points}
            </Typography>
          </Box>

          {/* Answer Display */}
          {question.question_type === 'short_text' ? (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Your Answer:
              </Typography>
              <Box
                sx={{
                  p: 2,
                  backgroundColor: theme.palette.grey[50],
                  borderRadius: 1,
                  border: `1px solid ${theme.palette.grey[300]}`,
                }}
              >
                <Typography variant="body2">
                  {question.user_answer || 'No answer provided'}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Box>
              <Typography variant="body2" color="text.secondary" gutterBottom>
                Your Answer:
              </Typography>
              {question.choices.map((choice) => (
                <Box
                  key={choice.id}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    p: 1.5,
                    mb: 1,
                    backgroundColor: choice.was_selected
                      ? alpha(theme.palette.primary.main, 0.08)
                      : 'transparent',
                    borderRadius: 1,
                    border: `1px solid ${
                      choice.was_selected
                        ? theme.palette.primary.main
                        : theme.palette.grey[300]
                    }`,
                  }}
                >
                  {question.question_type === 'multiple_choice' ||
                  question.question_type === 'true_false' ? (
                    choice.was_selected ? (
                      <RadioButtonChecked color="primary" fontSize="small" />
                    ) : (
                      <RadioButtonUnchecked color="disabled" fontSize="small" />
                    )
                  ) : (
                    // multi_select
                    choice.was_selected ? (
                      <CheckBoxIcon color="primary" fontSize="small" />
                    ) : (
                      <CheckBoxOutlineBlankIcon color="disabled" fontSize="small" />
                    )
                  )}
                  <Typography
                    variant="body2"
                    sx={{
                      fontWeight: choice.was_selected ? 600 : 400,
                    }}
                  >
                    {choice.text}
                  </Typography>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    )
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          {results?.is_passed ? (
            <CheckCircleIcon color="success" />
          ) : (
            <CancelIcon color="error" />
          )}
          Assessment Results - {assessmentTitle}
        </Box>
      </DialogTitle>

      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" py={4}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {results && (
          <>
            {/* Summary Card */}
            <Card sx={{ mb: 3, backgroundColor: alpha(theme.palette.primary.main, 0.05) }}>
              <CardContent>
                <Box display="flex" justifyContent="space-around" alignItems="center">
                  <Box textAlign="center">
                    <Typography
                      variant="h3"
                      fontWeight="bold"
                      color={results.is_passed ? 'success.main' : 'error.main'}
                    >
                      {results.score?.toFixed(1)}%
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Your Score
                    </Typography>
                  </Box>

                  <Divider orientation="vertical" flexItem />

                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight="bold">
                      {results.points_earned} / {results.total_points}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Points Earned
                    </Typography>
                  </Box>

                  <Divider orientation="vertical" flexItem />

                  <Box textAlign="center">
                    <Typography variant="h5" fontWeight="bold">
                      {results.questions.length}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Questions
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 2 }} />

                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Attempt Number: <strong>{results.attempt_number}</strong>
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    Submitted:{' '}
                    <strong>
                      {new Date(results.submitted_at).toLocaleString()}
                    </strong>
                  </Typography>
                </Box>
              </CardContent>
            </Card>

            {/* Question Results */}
            <Box>
              <Typography variant="h6" gutterBottom>
                Question-by-Question Results
              </Typography>
              <Alert severity="info" sx={{ mb: 2 }}>
                Below are the questions from your last attempt. Correct answers are marked
                in green, incorrect answers in red. Note: For security reasons, we don't
                show which specific choices were correct for wrong answers.
              </Alert>

              {results.questions.map((question, index) =>
                renderQuestionResult(question, index)
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default ShowResultsDialog
